import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, parseISO, isToday, isPast, isFuture } from 'date-fns';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

// Format date to Indian style: DD-MM-YYYY
const formatIndianDate = (dateString) => {
  try {
    const date = parseISO(dateString);
    return format(date, 'dd-MM-yyyy');
  } catch {
    return dateString;
  }
};

// Format time to 12-hour format
const formatIndianTime = (timeString) => {
  try {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeString;
  }
};

// Format time range
const formatTimeRange = (timeSlot) => {
  try {
    const [start, end] = timeSlot.split(' - ');
    return `${formatIndianTime(start)} - ${formatIndianTime(end)}`;
  } catch {
    return timeSlot;
  }
};

export default function MRAppointments() {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming, today, past, all
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appointments/mr`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppointments(response.data);
      setFilteredAppointments(response.data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const filterAppointments = useCallback(() => {
    let filtered = [...appointments];
    
    // Filter by date
    if (filter !== 'all') {
      filtered = filtered.filter(appt => {
        const apptDate = parseISO(appt.appointment_date);
        
        if (filter === 'today') {
          return isToday(apptDate);
        } else if (filter === 'upcoming') {
          return isFuture(apptDate) || isToday(apptDate);
        } else if (filter === 'past') {
          return isPast(apptDate) && !isToday(apptDate);
        }
        return true;
      });
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }
    
    // Sort: upcoming first (ascending), then past (descending)
    filtered.sort((a, b) => {
      const dateA = parseISO(a.appointment_date);
      const dateB = parseISO(b.appointment_date);
      
      if (filter === 'past') {
        return dateB - dateA; // Most recent past first
      } else {
        return dateA - dateB; // Nearest upcoming first
      }
    });
    
    setFilteredAppointments(filtered);
  }, [appointments, filter, statusFilter]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    filterAppointments();
  }, [filterAppointments]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="overflow-x-hidden">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-heading font-bold text-background-dark mb-3 sm:mb-4">My Appointments</h2>
        
        {/* Date Filter Tabs */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          <button
            onClick={() => setFilter('upcoming')}
            data-testid="filter-upcoming"
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              filter === 'upcoming'
                ? 'bg-primary text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setFilter('today')}
            data-testid="filter-today"
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              filter === 'today'
                ? 'bg-accent text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setFilter('past')}
            data-testid="filter-past"
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              filter === 'past'
                ? 'bg-slate-700 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Past
          </button>
          <button
            onClick={() => setFilter('all')}
            data-testid="filter-all"
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            All
          </button>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-xs sm:text-sm text-slate-600 font-medium">Status:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="mr-appointment-filter" className="w-28 sm:w-40 h-8 sm:h-10 text-xs sm:text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filteredAppointments.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-12 text-center">
          <p className="text-slate-600 text-sm sm:text-base">
            {filter === 'today' && 'No appointments scheduled for today.'}
            {filter === 'upcoming' && 'No upcoming appointments. Start by searching for doctors.'}
            {filter === 'past' && 'No past appointments yet.'}
            {filter === 'all' && 'No appointments found. Start by searching for doctors.'}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-900">{filteredAppointments.length}</span> appointment{filteredAppointments.length !== 1 ? 's' : ''}
              {filter !== 'all' && ` in ${filter === 'upcoming' ? 'Upcoming' : filter === 'today' ? 'Today' : 'Past'}`}
            </p>
          </div>
          
          <div className="space-y-3 sm:space-y-4">
          {filteredAppointments.map((appt) => (
            <div
              key={appt.id}
              className="bg-white rounded-xl border border-slate-200 p-3 sm:p-6"
              data-testid={`mr-appointment-${appt.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 flex-wrap">
                    <h3 className="font-heading font-bold text-sm sm:text-lg text-background-dark">
                      {appt.doctor_details?.full_name}
                    </h3>
                    <span
                      className={`px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${
                        appt.status === 'approved'
                          ? 'bg-accent/10 text-accent'
                          : appt.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-destructive/10 text-destructive'
                      }`}
                    >
                      {appt.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="text-xs sm:text-sm text-primary mb-1 sm:mb-2">
                    {appt.doctor_details?.specialization}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 mb-2 sm:mb-3">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="font-medium">{formatIndianDate(appt.appointment_date)}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="font-medium">{formatTimeRange(appt.time_slot)}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{appt.doctor_details?.clinic_address}</span>
                  </div>

                  {appt.notes && (
                    <div className="mt-3 text-sm text-slate-600">
                      <span className="font-medium">Notes:</span> {appt.notes}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}